// PowerComposer — markdown-as-you-type parsing, pure.
//
// Promoted from sketches/chat-composer/markdownSegments.ts (owner's explicit
// wishlist item: markdown renders live in the draft, not only in the sent
// message). Powers the mirror-overlay technique in PowerComposer.tsx: a
// styled `<pre>` mirror paints these segments while the real `<textarea>`
// underneath stays the one source of truth (transparent text, visible
// caret). Deliberately a SUBSET of markdown — no nesting, no headings, no
// blockquotes, links only as the `[title](href)` @-mention token (D8) — enough to cover the task's five marks (bold,
// italic, inline code, list items, fenced code blocks) without growing into
// a full grammar a `<textarea>` was never built to host.
//
// KNOWN LIMITATION (stated, not hidden — see PowerComposer.tsx's header for
// the full explanation): the mirror and the invisible textarea stay
// pixel-aligned only while every segment shares the plain-text glyph
// metrics. Bold (font-weight 600) and monospace `code`/fenced code-block
// lines both shift character widths in a proportional typeface, so the
// caret can drift a few px from the styled glyph beneath it on those spans.
// Italic and list markers use colour/decoration only (no width-shifting
// channel), so they stay perfectly aligned. This is a structural cost of
// the overlay technique, not a bug to chase away — a production fix needs a
// real rich-text editor (ProseMirror/Lexical-class dependency) or a
// monospace-locked font across every style.
//
// Pure throughout: same input always yields the same output, no I/O.

export type MarkdownSegment =
    | { readonly type: "plain"; readonly text: string }
    | { readonly type: "bold"; readonly text: string }
    | { readonly type: "italic"; readonly text: string }
    | { readonly type: "code"; readonly text: string }
    // A `[title](href)` link: the @-mention token (D8). Colour only, so it
    // stays aligned: the title is highlighted, `[` and `](href)` dimmed.
    | { readonly type: "link"; readonly text: string; readonly title: string };

// Matches one CLOSED [link](href), **bold**, `code`, or *italic* span. The syntax
// characters ride INSIDE the segment's `text` (the caller dims them rather
// than stripping them), so segment lengths always sum back to the exact
// source string — the mirror stays character-for-character aligned with the
// textarea underneath it.
const INLINE_TOKEN =
    /(\[[^\]\n]+\]\([^)\s]+\)|\*\*[^*\n]+\*\*|`[^`\n]+`|\*[^*\n]+\*)/g;
const LINK_TITLE = /^\[([^\]\n]+)\]\(/;

export const parseInlineMarkdown = (
    text: string,
): readonly MarkdownSegment[] => {
    const segments: MarkdownSegment[] = [];
    let cursor = 0;
    for (const match of text.matchAll(INLINE_TOKEN)) {
        const start = match.index ?? 0;
        if (start > cursor) {
            segments.push({ type: "plain", text: text.slice(cursor, start) });
        }
        const token = match[0];
        const link = LINK_TITLE.exec(token);
        if (link !== null) {
            segments.push({ type: "link", text: token, title: link[1] ?? "" });
        } else if (token.startsWith("**")) {
            segments.push({ type: "bold", text: token });
        } else if (token.startsWith("`")) {
            segments.push({ type: "code", text: token });
        } else {
            segments.push({ type: "italic", text: token });
        }
        cursor = start + token.length;
    }
    if (cursor < text.length) {
        segments.push({ type: "plain", text: text.slice(cursor) });
    }
    return segments;
};

// One line of the draft, classified for BLOCK-level rendering (list items,
// fenced code) before its remaining text runs through the inline parser
// above. A named sum, not a bag of booleans: a line is EXACTLY one of these
// four things, matching how the mirror renders it.
export type MarkdownLine =
    // A ``` fence line itself — rendered as a dimmed marker, never styled as
    // code (it is punctuation, not content).
    | { readonly type: "code-fence"; readonly text: string }
    // A line INSIDE an open fence — rendered as one monospace span (the
    // fenced-code-block caret-drift case named in the header above).
    | { readonly type: "code-body"; readonly text: string }
    // A bulleted/numbered list line. `marker` (e.g. "- ", "12. ") is dimmed;
    // `segments` is the inline-parsed rest of the line. `marker + segments'
    // source text === the original line, so nothing is lost.
    | {
          readonly type: "list-item";
          readonly marker: string;
          readonly segments: readonly MarkdownSegment[];
      }
    // An ordinary paragraph line — inline-parsed, no block marker.
    | { readonly type: "text"; readonly segments: readonly MarkdownSegment[] };

// "- ", "* ", "12. " — one leading bullet or ordinal followed by exactly one
// space. Single-level only (no nested indentation) — this is the composer's
// live-typing cue, not a list-authoring surface.
const LIST_MARKER = /^([-*]|\d+\.)( )/;
const CODE_FENCE = /^```/;

// Splits a draft into its lines (`text.split("\n")`) and classifies each one.
// `text.split("\n")` then re-joining with "\n" always reproduces the exact
// source string (including a trailing empty line), which is what keeps the
// mirror's rendered text content identical to the textarea's — the
// precondition for the overlay to stay usable at all.
export const parseMarkdownLines = (text: string): readonly MarkdownLine[] => {
    const lines: MarkdownLine[] = [];
    let inFence = false;
    for (const raw of text.split("\n")) {
        if (CODE_FENCE.test(raw)) {
            lines.push({ type: "code-fence", text: raw });
            inFence = !inFence;
            continue;
        }
        if (inFence) {
            lines.push({ type: "code-body", text: raw });
            continue;
        }
        const listMatch = LIST_MARKER.exec(raw);
        if (listMatch !== null) {
            const marker = listMatch[0];
            lines.push({
                type: "list-item",
                marker,
                segments: parseInlineMarkdown(raw.slice(marker.length)),
            });
            continue;
        }
        lines.push({ type: "text", segments: parseInlineMarkdown(raw) });
    }
    return lines;
};
